/** Shared shader for painted depth layers: mood crossfade, depth displacement (backdrop only),
 * edge-safe sampling, Dread lighting and a card-impact ripple that reaches each layer at a different time. */
export const layerVertex = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const layerFragment = /* glsl */ `
uniform sampler2D uTex;
uniform sampler2D uPrev;
uniform sampler2D uDepth;
uniform float uHasDepth;
uniform float uMix;
uniform vec2 uLook;
uniform float uDisplace;
uniform float uTime;
uniform float uLight;      // overall candle light (dread + flicker)
uniform vec3 uTint;
uniform float uImpact;     // seconds since impact reached this layer (<0 = not yet)
uniform vec2 uImpactUv;
uniform float uRipple;     // 0 when reduced motion
uniform float uDarken;     // foreground props sit in shadow
varying vec2 vUv;

vec4 sampleBoth(vec2 uv) {
  vec4 a = texture2D(uPrev, uv);
  vec4 b = texture2D(uTex, uv);
  return mix(a, b, clamp(uMix, 0.0, 1.0));
}

void main() {
  vec2 uv = vUv;
  if (uHasDepth > 0.5) {
    float d = texture2D(uDepth, uv).r;
    // shrink displacement near strong depth edges so outlines do not smear
    float dx = abs(texture2D(uDepth, uv + vec2(0.004, 0.0)).r - texture2D(uDepth, uv - vec2(0.004, 0.0)).r);
    float dy = abs(texture2D(uDepth, uv + vec2(0.0, 0.004)).r - texture2D(uDepth, uv - vec2(0.0, 0.004)).r);
    float edge = 1.0 - smoothstep(0.02, 0.12, dx + dy);
    uv += uLook * (d - 0.4) * uDisplace * edge;
  }

  // impact ripple: a ring that expands from the impact point, with a chromatic split
  float env = 0.0;
  vec2 dir = vec2(0.0);
  if (uImpact > 0.0 && uImpact < 1.6) {
    float t = uImpact;
    vec2 rel = uv - uImpactUv;
    rel.x *= 1.6;
    float dist = length(rel);
    float ring = exp(-pow((dist - t * 0.9) * 9.0, 2.0));
    env = ring * (1.0 - t / 1.6) * uRipple;
    dir = normalize(rel + 1e-5);
    uv += dir * env * 0.035;
  }
  uv = clamp(uv, vec2(0.002), vec2(0.998));

  vec4 col;
  if (env > 0.001) {
    float split = env * 0.012;
    vec4 c0 = sampleBoth(uv + dir * split);
    vec4 c1 = sampleBoth(uv);
    vec4 c2 = sampleBoth(uv - dir * split);
    col = vec4(c0.r, c1.g, c2.b, max(c1.a, max(c0.a, c2.a)));
    col.rgb += vec3(1.0, 0.75, 0.35) * env * 0.35; // ink-light repaint flash
  } else {
    col = sampleBoth(uv);
  }
  col.rgb *= uTint * uLight * (1.0 - uDarken);
  if (col.a < 0.01) discard;
  gl_FragColor = col;
  #include <colorspace_fragment>
}
`;
